import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { useNavigate } from "react-router";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import { AlertTriangle, Info, ListMusic, Network, RotateCcw } from "lucide-react";
import type { Filter } from "#shared/schemas/filter.ts";
import {
  exploreEdgeKey,
  exploreNodeKey,
  exploreSeedKey,
  type ExploreEdge,
  type ExplorePlaylistKind,
  type ExploreArtistNode,
  type ExplorePlaylistNode,
} from "#shared/schemas/playlistExplore.ts";
import { Button } from "@web/components/ui/button.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import {
  fetchExploreArtistPlaylists,
  fetchExplorePlaylistArtists,
} from "@web/features/library/playlistExploreApi.ts";
import { cn } from "@web/lib/utils.ts";
import {
  emptySession,
  usePlaylistExploreStore,
  type ExploreSession,
} from "@web/stores/playlistExplore.ts";
import { toast } from "sonner";
import { ApiRequestError } from "@web/lib/api.ts";

const PAGE = 25;
const NODE_WARN = 150;
const MIN_SCALE = 0.35;
const MAX_SCALE = 3;
/** Soft reheat when a few nodes join an existing graph. */
const REHEAT_ALPHA = 0.22;
/** Stronger first settle so a fresh star graph opens into a ring, not a clump. */
const FRESH_ALPHA = 0.85;
const CLICK_DETAIL_MS = 280;
const GHOST_CAP = 8;
const GHOST_FADE_MS = 150;
/** Default orbit radius for a fresh playlist→artists ring. */
const SEED_RING_RADIUS = 170;

class ImageCache {
  private cache = new Map<string, HTMLImageElement | "loading" | "error">();

  get(url: string | null | undefined, onReady?: () => void): HTMLImageElement | null {
    if (!url) return null;
    const hit = this.cache.get(url);
    if (hit === "loading" || hit === "error") return null;
    if (hit instanceof HTMLImageElement) return hit.complete && hit.naturalWidth > 0 ? hit : null;
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    this.cache.set(url, "loading");
    img.onload = () => {
      this.cache.set(url, img);
      onReady?.();
    };
    img.onerror = () => {
      this.cache.set(url, "error");
    };
    return null;
  }
}

type NodeKind = "artist" | ExplorePlaylistKind;

interface PeekGhost {
  key: string;
  kind: NodeKind;
  id: string;
  name: string | null;
  image_url: string | null;
  track_count: number;
  x: number;
  y: number;
}

interface PeekState {
  parentKey: string;
  ghosts: PeekGhost[];
  more: number;
  /** 0…1 fade for enter/leave. */
  opacity: number;
}

interface SimNode extends SimulationNodeDatum {
  key: string;
  kind: NodeKind;
  id: string;
  name: string | null;
  image_url: string | null;
  track_count: number;
  radius: number;
}

interface SimLink extends SimulationLinkDatum<SimNode> {
  key: string;
  track_count: number;
}

interface Camera {
  x: number;
  y: number;
  scale: number;
}

interface GraphColors {
  accent: string;
  panel: string;
  playlist: string;
  playlistBorder: string;
  line: string;
  text: string;
  muted: string;
  faint: string;
  font: string;
}

function readColors(el: HTMLElement | null): GraphColors {
  const s = el ? getComputedStyle(el) : getComputedStyle(document.documentElement);
  return {
    accent: s.getPropertyValue("--color-accent").trim() || "#1ed760",
    panel: s.getPropertyValue("--color-panel").trim() || "#181818",
    playlist: s.getPropertyValue("--color-graph-playlist").trim() || "#2e2e2e",
    playlistBorder: s.getPropertyValue("--color-graph-playlist-border").trim() || "#535353",
    line: s.getPropertyValue("--color-line").trim() || "#282828",
    text: s.getPropertyValue("--color-text").trim() || "#ffffff",
    muted: s.getPropertyValue("--color-muted").trim() || "#b3b3b3",
    faint: s.getPropertyValue("--color-faint").trim() || "#6a6a6a",
    font: s.getPropertyValue("--font-sans").trim() || "sans-serif",
  };
}

function nodeRadius(kind: NodeKind, trackCount: number, maxTrack: number): number {
  const t = Math.sqrt(trackCount / Math.max(maxTrack, 1));
  if (kind === "artist") return 10 + t * 14;
  return 14 + t * 10;
}

/** Even ring around a parent — start at 12 o'clock, clockwise. */
function ringPositions(
  cx: number,
  cy: number,
  count: number,
  radius: number,
): Array<{ x: number; y: number }> {
  if (count <= 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / count;
    return {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    };
  });
}

function applyExpandResult(
  session: ExploreSession,
  parentKey: string,
  nodes: Array<ExploreArtistNode | ExplorePlaylistNode>,
  edges: ExploreEdge[],
  loaded: number,
  total: number,
  spawnNear: { x: number; y: number } | null,
  /** Written with even ring coords for brand-new nodes (not pins — force may settle). */
  spawnInto: Record<string, { x: number; y: number }>,
): ExploreSession {
  const nodeKeys = new Set(session.nodeKeys);
  const edgeKeys = new Set(session.edgeKeys);
  const nodeMeta = { ...session.nodeMeta };
  const edgeMeta = { ...session.edgeMeta };
  const pinned = { ...session.pinned };
  const introduced: string[] = [];

  const newcomers = nodes.filter((node) => !nodeKeys.has(exploreNodeKey(node)));
  const ringRadius = Math.max(
    120,
    (spawnNear ? SEED_RING_RADIUS : 110) + Math.min(90, newcomers.length * 3),
  );
  const spots =
    spawnNear && newcomers.length > 0
      ? ringPositions(spawnNear.x, spawnNear.y, newcomers.length, ringRadius)
      : [];
  let spotIndex = 0;

  for (const node of nodes) {
    const key = exploreNodeKey(node);
    if (!nodeKeys.has(key)) {
      nodeKeys.add(key);
      introduced.push(key);
      const spot = spots[spotIndex];
      spotIndex += 1;
      // Prefer peek-ghost positions already written into spawnInto.
      if (spot && spawnInto[key] === undefined) spawnInto[key] = spot;
    }
    nodeMeta[key] = {
      kind: node.kind,
      id: node.id,
      name: node.name,
      image_url: node.image_url,
      track_count: node.track_count,
    };
  }

  for (const edge of edges) {
    const key = exploreEdgeKey(edge);
    if (!edgeKeys.has(key)) {
      edgeKeys.add(key);
      introduced.push(key);
    }
    edgeMeta[key] = edge;
  }

  const expansions = session.expansions.filter((e) => e.id !== parentKey);
  const existing = session.expansions.find((e) => e.id === parentKey);
  const mergedIntroduced = [
    ...new Set([...(existing?.introducedKeys ?? []), ...introduced]),
  ];
  expansions.push({
    id: parentKey,
    parentKey,
    introducedKeys: mergedIntroduced,
    loaded,
    total,
  });

  const expansionOrder = session.expansionOrder.filter((id) => id !== parentKey);
  expansionOrder.push(parentKey);

  return {
    ...session,
    nodeKeys: [...nodeKeys],
    edgeKeys: [...edgeKeys],
    nodeMeta,
    edgeMeta,
    pinned,
    expansions,
    expansionOrder,
  };
}

function collapseExpansion(session: ExploreSession, parentKey: string): ExploreSession {
  if (!session.expansions.some((e) => e.id === parentKey)) return session;

  const seedKey = exploreSeedKey(session.seedKind, session.seedId);

  function introducersOf(key: string) {
    return session.expansions.filter((e) => e.introducedKeys.includes(key));
  }

  /** A node survives while the seed itself, or any non-collapsed expansion still claims it. */
  function nodeSurvives(nodeKey: string, collapsed: ReadonlySet<string>): boolean {
    if (nodeKey === seedKey) return true;
    const intro = introducersOf(nodeKey);
    if (intro.length === 0) return false;
    return intro.some((e) => !collapsed.has(e.id));
  }

  const collapsed = new Set<string>([parentKey]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const exp of session.expansions) {
      if (collapsed.has(exp.id)) continue;
      // Expansion of a node we're removing must go with it — otherwise its
      // introduced children lose every edge and float.
      if (!nodeSurvives(exp.parentKey, collapsed)) {
        collapsed.add(exp.id);
        grew = true;
      }
    }
  }

  const surviving = session.expansions.filter((e) => !collapsed.has(e.id));
  const keepNodes = session.nodeKeys.filter((key) => nodeSurvives(key, collapsed));
  const nodeSet = new Set(keepNodes);

  const edgeKeys = session.edgeKeys.filter((ek) => {
    const edge = session.edgeMeta[ek];
    if (!edge) return false;
    const aKey = exploreNodeKey({ kind: "artist", id: edge.artist_id });
    const pKey = exploreNodeKey({ kind: edge.playlist_kind, id: edge.playlist_id });
    if (!nodeSet.has(aKey) || !nodeSet.has(pKey)) return false;
    const intro = introducersOf(ek);
    if (intro.length === 0) return true;
    return intro.some((e) => !collapsed.has(e.id));
  });

  const pinned = { ...session.pinned };
  for (const key of Object.keys(pinned)) {
    if (!nodeSet.has(key)) delete pinned[key];
  }

  return {
    ...session,
    nodeKeys: keepNodes,
    edgeKeys,
    expansions: surviving,
    expansionOrder: session.expansionOrder.filter((id) => !collapsed.has(id)),
    pinned,
  };
}

function isExpanded(session: ExploreSession, key: string): boolean {
  return session.expansions.some((e) => e.id === key);
}

/** Fan ghost nodes around a parent, preferring the outward side from the origin. */
function layoutGhosts(
  parent: { x: number; y: number; radius: number },
  count: number,
): Array<{ x: number; y: number }> {
  if (count <= 0) return [];
  const outward = Math.atan2(parent.y, parent.x);
  const arc = Math.min(Math.PI * 0.9, 0.45 + count * 0.12);
  const start = outward - arc / 2;
  const dist = parent.radius + 52;
  return Array.from({ length: count }, (_, i) => {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const angle = start + arc * t;
    return {
      x: parent.x + Math.cos(angle) * dist,
      y: parent.y + Math.sin(angle) * dist,
    };
  });
}

function buildOpenTracksFilter(session: ExploreSession, selected: string[]): Filter | null {
  const artistIds: string[] = [];
  const playlistIds: string[] = [];
  const localIds: string[] = [];
  for (const key of selected) {
    const meta = session.nodeMeta[key];
    if (!meta) continue;
    if (meta.kind === "artist") artistIds.push(meta.id);
    else if (meta.kind === "playlist") playlistIds.push(meta.id);
    else localIds.push(meta.id);
  }
  const nodes: Filter[] = [];
  if (artistIds.length === 1) nodes.push({ field: "artist.id", cmp: "eq", value: artistIds[0]! });
  else if (artistIds.length > 1) nodes.push({ field: "artist.id", cmp: "in", value: artistIds });
  if (playlistIds.length === 1) nodes.push({ field: "playlist.id", cmp: "eq", value: playlistIds[0]! });
  else if (playlistIds.length > 1)
    nodes.push({ field: "playlist.id", cmp: "in", value: playlistIds });
  if (localIds.length === 1) nodes.push({ field: "local.id", cmp: "eq", value: localIds[0]! });
  else if (localIds.length > 1) nodes.push({ field: "local.id", cmp: "in", value: localIds });
  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0]!;
  return { op: "or", nodes };
}

export function PlaylistExplorer({
  kind,
  id,
}: {
  kind: ExplorePlaylistKind;
  id: string;
}) {
  const navigate = useNavigate();
  const openDetail = useOpenDetail();
  const stored = usePlaylistExploreStore((s) => s.getSession(kind, id));
  const upsert = usePlaylistExploreStore((s) => s.upsertSession);
  const clearSession = usePlaylistExploreStore((s) => s.clearSession);
  const setPinned = usePlaylistExploreStore((s) => s.setPinned);

  const [session, setSession] = useState<ExploreSession>(
    () => stored ?? emptySession(kind, id, "Playlist"),
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [bootstrapping, setBootstrapping] = useState(!stored || stored.expansions.length === 0);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [peek, setPeek] = useState<PeekState | null>(null);

  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const nodesRef = useRef<SimNode[]>([]);
  const linksRef = useRef<SimLink[]>([]);
  const cameraRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const colorsRef = useRef<GraphColors>(readColors(null));
  const dragRef = useRef<{
    mode: "pan" | "node";
    key?: string;
    lastX: number;
    lastY: number;
    moved: boolean;
  } | null>(null);
  const lastClickRef = useRef<{ key: string; at: number } | null>(null);
  const detailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imageCacheRef = useRef(new ImageCache());
  const pendingSpawnRef = useRef<Record<string, { x: number; y: number }>>({});
  const peekCacheRef = useRef(
    new Map<string, { ghosts: Omit<PeekGhost, "x" | "y">[]; more: number }>(),
  );
  const peekFetchRef = useRef(0);
  const hoverKeyRef = useRef<string | null>(null);
  const peekFadeRef = useRef<number | null>(null);
  const peekRef = useRef<PeekState | null>(null);
  peekRef.current = peek;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const persist = useCallback(
    (next: ExploreSession) => {
      setSession(next);
      upsert(next);
    },
    [upsert],
  );

  // Seed bootstrap — first expansion of the centre playlist.
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (stored && stored.expansions.length > 0) {
        setSession(stored);
        setBootstrapping(false);
        return;
      }
      setBootstrapping(true);
      try {
        const data = await fetchExplorePlaylistArtists(kind, id, 0, PAGE);
        if (cancelled) return;
        const seedKey = exploreSeedKey(kind, id);
        let next = emptySession(kind, id, data.playlist.name ?? "Playlist");
        next.nodeMeta[seedKey] = {
          kind: data.playlist.kind,
          id: data.playlist.id,
          name: data.playlist.name,
          image_url: data.playlist.image_url,
          track_count: data.playlist.track_count,
        };
        next = applyExpandResult(
          next,
          seedKey,
          data.artists,
          data.edges,
          data.artists.length,
          data.total,
          { x: 0, y: 0 },
          pendingSpawnRef.current,
        );
        // Seed playlist stays pinned at the origin; artists orbit on a ring (not pinned).
        next = {
          ...next,
          pinned: { ...next.pinned, [seedKey]: { x: 0, y: 0 } },
        };
        persist(next);
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiRequestError ? err.message : "Failed to load explorer");
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per route id
  }, [kind, id]);

  // Hover peek — faded satellites of playlists/artists not yet on the canvas.
  useEffect(() => {
    hoverKeyRef.current = hoveredKey;
    if (peekFadeRef.current !== null) {
      cancelAnimationFrame(peekFadeRef.current);
      peekFadeRef.current = null;
    }

    if (!hoveredKey) {
      const current = peekRef.current;
      if (!current || current.opacity <= 0) {
        setPeek(null);
        return;
      }
      const start = performance.now();
      const from = current.opacity;
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / GHOST_FADE_MS);
        const nextOp = from * (1 - t);
        if (t >= 1) {
          setPeek(null);
          peekFadeRef.current = null;
          return;
        }
        setPeek((prev) => (prev ? { ...prev, opacity: nextOp } : null));
        peekFadeRef.current = requestAnimationFrame(tick);
      };
      peekFadeRef.current = requestAnimationFrame(tick);
      return () => {
        if (peekFadeRef.current !== null) cancelAnimationFrame(peekFadeRef.current);
      };
    }

    const parent = nodesRef.current.find((n) => n.key === hoveredKey);
    if (!parent || parent.x === undefined || parent.y === undefined) {
      setPeek(null);
      return;
    }
    // Already expanded — the real nodes are the reveal; no ghost ring.
    if (isExpanded(sessionRef.current, hoveredKey)) {
      setPeek(null);
      return;
    }

    const requestId = ++peekFetchRef.current;
    const parentSnapshot = {
      x: parent.x,
      y: parent.y,
      radius: parent.radius,
      kind: parent.kind,
      id: parent.id,
      key: parent.key,
    };

    void (async () => {
      try {
        const present = new Set(sessionRef.current.nodeKeys);
        let ghosts: Omit<PeekGhost, "x" | "y">[] = [];
        let more = 0;

        const cached = peekCacheRef.current.get(parentSnapshot.key);
        if (cached) {
          ghosts = cached.ghosts.filter((g) => !present.has(g.key)).slice(0, GHOST_CAP);
          more = cached.more;
        } else if (parentSnapshot.kind === "artist") {
          // Over-fetch a bit so filtering out the seed playlist still leaves satellites.
          const data = await fetchExploreArtistPlaylists(parentSnapshot.id, 0, 24);
          const all = data.playlists.map((pl) => ({
            key: exploreNodeKey(pl),
            kind: pl.kind as NodeKind,
            id: pl.id,
            name: pl.name,
            image_url: pl.image_url,
            track_count: pl.track_count,
          }));
          peekCacheRef.current.set(parentSnapshot.key, {
            ghosts: all,
            more: Math.max(0, data.total - all.length),
          });
          ghosts = all.filter((g) => !present.has(g.key)).slice(0, GHOST_CAP);
          more = Math.max(0, data.total - present.size - ghosts.length);
        } else {
          const data = await fetchExplorePlaylistArtists(
            parentSnapshot.kind,
            parentSnapshot.id,
            0,
            24,
          );
          const all = data.artists.map((artist) => ({
            key: exploreNodeKey(artist),
            kind: "artist" as const,
            id: artist.id,
            name: artist.name,
            image_url: artist.image_url,
            track_count: artist.track_count,
          }));
          peekCacheRef.current.set(parentSnapshot.key, {
            ghosts: all,
            more: Math.max(0, data.total - all.length),
          });
          ghosts = all.filter((g) => !present.has(g.key)).slice(0, GHOST_CAP);
          more = Math.max(0, data.total - present.size - ghosts.length);
        }

        if (peekFetchRef.current !== requestId || hoverKeyRef.current !== parentSnapshot.key) {
          return;
        }
        if (ghosts.length === 0) {
          setPeek(null);
          return;
        }

        const spots = layoutGhosts(parentSnapshot, ghosts.length);
        const placed: PeekGhost[] = ghosts.map((g, i) => ({
          ...g,
          x: spots[i]?.x ?? parentSnapshot.x,
          y: spots[i]?.y ?? parentSnapshot.y,
        }));

        setPeek({
          parentKey: parentSnapshot.key,
          ghosts: placed,
          more,
          opacity: 0,
        });

        const start = performance.now();
        const tick = (now: number) => {
          if (hoverKeyRef.current !== parentSnapshot.key) return;
          const t = Math.min(1, (now - start) / GHOST_FADE_MS);
          setPeek((prev) =>
            prev && prev.parentKey === parentSnapshot.key ? { ...prev, opacity: t } : prev,
          );
          if (t < 1) peekFadeRef.current = requestAnimationFrame(tick);
          else peekFadeRef.current = null;
        };
        peekFadeRef.current = requestAnimationFrame(tick);
      } catch {
        // Peek is optional — expand still works without it.
      }
    })();

    return () => {
      if (peekFadeRef.current !== null) cancelAnimationFrame(peekFadeRef.current);
    };
  }, [hoveredKey]);

  // If an expand lands while hover was still on that node, kill leftover peeks.
  useEffect(() => {
    if (!peek) return;
    if (isExpanded(session, peek.parentKey)) {
      setPeek(null);
      if (hoveredKey === peek.parentKey) setHoveredKey(null);
    }
  }, [session.expansions, peek, hoveredKey]);

  const maxTrack = useMemo(() => {
    let max = 1;
    for (const key of session.nodeKeys) {
      const meta = session.nodeMeta[key];
      if (meta && meta.track_count > max) max = meta.track_count;
    }
    for (const key of session.edgeKeys) {
      const edge = session.edgeMeta[key];
      if (edge && edge.track_count > max) max = edge.track_count;
    }
    return max;
  }, [session]);

  // Rebuild / sync force sim when membership or pins change.
  useEffect(() => {
    const existing = new Map(nodesRef.current.map((n) => [n.key, n]));
    const hadExisting = existing.size > 0;

    // Cold open / restore: park free nodes on an even ring so we don't open as a clump.
    if (!hadExisting) {
      const freeKeys = session.nodeKeys.filter(
        (key) =>
          session.nodeMeta[key] &&
          !session.pinned[key] &&
          !pendingSpawnRef.current[key],
      );
      if (freeKeys.length > 0) {
        const spots = ringPositions(0, 0, freeKeys.length, SEED_RING_RADIUS);
        freeKeys.forEach((key, i) => {
          const spot = spots[i];
          if (spot) pendingSpawnRef.current[key] = spot;
        });
      }
    }

    const nextNodes: SimNode[] = [];
    let added = 0;

    for (const key of session.nodeKeys) {
      const meta = session.nodeMeta[key];
      if (!meta) continue;
      const prev = existing.get(key);
      const pin = session.pinned[key];
      const spawn = pendingSpawnRef.current[key];
      const radius = nodeRadius(meta.kind, meta.track_count, maxTrack);
      if (prev) {
        prev.kind = meta.kind;
        prev.id = meta.id;
        prev.name = meta.name;
        prev.image_url = meta.image_url;
        prev.track_count = meta.track_count;
        prev.radius = radius;
        if (pin) {
          prev.fx = pin.x;
          prev.fy = pin.y;
        } else {
          prev.fx = undefined;
          prev.fy = undefined;
        }
        nextNodes.push(prev);
      } else {
        added += 1;
        const introducer = session.expansions.find((e) => e.introducedKeys.includes(key));
        const parent = introducer ? existing.get(introducer.parentKey) : undefined;
        const node: SimNode = {
          key,
          kind: meta.kind,
          id: meta.id,
          name: meta.name,
          image_url: meta.image_url,
          track_count: meta.track_count,
          radius,
          x: pin?.x ?? spawn?.x ?? parent?.x ?? 0,
          y: pin?.y ?? spawn?.y ?? parent?.y ?? 0,
        };
        if (pin) {
          node.fx = pin.x;
          node.fy = pin.y;
        }
        nextNodes.push(node);
        delete pendingSpawnRef.current[key];
      }
    }
    nodesRef.current = nextNodes;
    const byKey = new Map(nextNodes.map((n) => [n.key, n]));

    const nextLinks: SimLink[] = [];
    for (const ek of session.edgeKeys) {
      const edge = session.edgeMeta[ek];
      if (!edge) continue;
      const source = byKey.get(exploreNodeKey({ kind: "artist", id: edge.artist_id }));
      const target = byKey.get(
        exploreNodeKey({ kind: edge.playlist_kind, id: edge.playlist_id }),
      );
      if (!source || !target) continue;
      nextLinks.push({
        key: ek,
        source,
        target,
        track_count: edge.track_count,
      });
    }
    linksRef.current = nextLinks;

    const freshStar = !hadExisting || (existing.size <= 1 && added > 3);
    const linkDistance = freshStar ? SEED_RING_RADIUS : 130;

    simRef.current?.stop();
    const sim = forceSimulation<SimNode>(nextNodes)
      .force(
        "link",
        forceLink<SimNode, SimLink>(nextLinks)
          .id((d) => d.key)
          .distance(linkDistance)
          .strength(0.55),
      )
      .force(
        "charge",
        forceManyBody<SimNode>()
          .strength(-420)
          .distanceMax(520),
      )
      .force("center", forceCenter(0, 0).strength(0.015))
      .force(
        "collide",
        forceCollide<SimNode>()
          .radius((d) => d.radius + 16)
          .strength(0.95)
          .iterations(3),
      )
      .alpha(freshStar ? FRESH_ALPHA : added > 0 ? REHEAT_ALPHA : 0.05)
      .alphaDecay(freshStar ? 0.022 : 0.04)
      .on("tick", draw);

    simRef.current = sim;
    return () => {
      sim.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session.nodeKeys.join("|"),
    session.edgeKeys.join("|"),
    JSON.stringify(session.pinned),
    maxTrack,
  ]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    colorsRef.current = readColors(wrap);
    const colors = colorsRef.current;
    const cam = cameraRef.current;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.save();
    ctx.translate(w / 2 + cam.x, h / 2 + cam.y);
    ctx.scale(cam.scale, cam.scale);

    const maxEdge = Math.max(1, ...linksRef.current.map((l) => l.track_count));
    const peekNow = peek;
    const hoverId = hoveredKey;

    for (const link of linksRef.current) {
      const s = link.source as SimNode;
      const t = link.target as SimNode;
      if (s.x === undefined || s.y === undefined || t.x === undefined || t.y === undefined) continue;
      const width = 1 + (link.track_count / maxEdge) * 4;
      const hot =
        hoverId !== null && (s.key === hoverId || t.key === hoverId);
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
      ctx.strokeStyle = hot ? colors.accent : colors.line;
      let alpha = 0.55 + (link.track_count / maxEdge) * 0.35;
      if (peekNow && !hot) alpha *= 0.28;
      else if (hot) alpha = Math.max(alpha, 0.9);
      ctx.globalAlpha = alpha;
      ctx.lineWidth = width / cam.scale;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    for (const node of nodesRef.current) {
      if (node.x === undefined || node.y === undefined) continue;
      const isSel = selected.includes(node.key);
      const expanded = isExpanded(session, node.key);
      const isHover = node.key === hoverId;
      const img = imageCacheRef.current.get(node.image_url, () => {
        requestAnimationFrame(() => draw());
      });

      let baseAlpha = 1;
      if (peekNow && !isHover) baseAlpha = 0.32;
      else if (hoverId && !isHover && !peekNow) baseAlpha = 0.55;
      ctx.globalAlpha = baseAlpha;

      if (node.kind === "artist") {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        if (img) {
          ctx.save();
          ctx.clip();
          ctx.drawImage(
            img,
            node.x - node.radius,
            node.y - node.radius,
            node.radius * 2,
            node.radius * 2,
          );
          ctx.restore();
        } else {
          ctx.fillStyle = colors.panel;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.lineWidth = (isSel || expanded || isHover ? 2.5 : 1.25) / cam.scale;
        ctx.strokeStyle = isSel || isHover ? colors.accent : expanded ? colors.muted : colors.line;
        ctx.stroke();
      } else {
        const side = node.radius * 1.7;
        const x0 = node.x - side / 2;
        const y0 = node.y - side / 2;
        const r = Math.min(4 / cam.scale, side / 4);
        ctx.beginPath();
        roundRect(ctx, x0, y0, side, side, r);
        if (img) {
          ctx.save();
          ctx.clip();
          ctx.drawImage(img, x0, y0, side, side);
          ctx.restore();
        } else {
          ctx.fillStyle = colors.playlist;
          ctx.fill();
        }
        ctx.beginPath();
        roundRect(ctx, x0, y0, side, side, r);
        ctx.lineWidth = (isSel || expanded || isHover ? 2.5 : 1.25) / cam.scale;
        ctx.strokeStyle = isSel || isHover
          ? colors.accent
          : expanded
            ? colors.muted
            : colors.playlistBorder;
        ctx.stroke();
      }

      if (cam.scale >= 0.7) {
        const label = node.name ?? "Untitled";
        ctx.font = `${12 / cam.scale}px ${colors.font}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillStyle = colors.text;
        ctx.globalAlpha = baseAlpha * 0.9;
        const labelY =
          node.kind === "artist"
            ? node.y + node.radius + 4 / cam.scale
            : node.y + node.radius * 0.85 + 4 / cam.scale;
        ctx.fillText(label, node.x, labelY);
      }
      ctx.globalAlpha = 1;
    }

    // Faded satellite peeks — not in the force graph until expanded.
    // Never draw peeks for a node that's already expanded (stale hover).
    if (
      peekNow &&
      peekNow.opacity > 0.01 &&
      !isExpanded(session, peekNow.parentKey)
    ) {
      const parent = nodesRef.current.find((n) => n.key === peekNow.parentKey);
      const ghostAlpha = peekNow.opacity * 0.42;
      for (const ghost of peekNow.ghosts) {
        if (parent && parent.x !== undefined && parent.y !== undefined) {
          ctx.beginPath();
          ctx.moveTo(parent.x, parent.y);
          ctx.lineTo(ghost.x, ghost.y);
          ctx.strokeStyle = colors.line;
          ctx.globalAlpha = ghostAlpha * 0.7;
          ctx.lineWidth = 1 / cam.scale;
          ctx.stroke();
        }

        const r = ghost.kind === "artist" ? 7 : 8;
        const img = imageCacheRef.current.get(ghost.image_url, () => {
          requestAnimationFrame(() => draw());
        });
        ctx.globalAlpha = ghostAlpha;
        if (ghost.kind === "artist") {
          ctx.beginPath();
          ctx.arc(ghost.x, ghost.y, r, 0, Math.PI * 2);
          if (img) {
            ctx.save();
            ctx.clip();
            ctx.drawImage(img, ghost.x - r, ghost.y - r, r * 2, r * 2);
            ctx.restore();
          } else {
            ctx.fillStyle = colors.panel;
            ctx.fill();
          }
          ctx.beginPath();
          ctx.arc(ghost.x, ghost.y, r, 0, Math.PI * 2);
          ctx.strokeStyle = colors.faint;
          ctx.lineWidth = 1 / cam.scale;
          ctx.stroke();
        } else {
          const side = r * 1.7;
          ctx.beginPath();
          roundRect(ctx, ghost.x - side / 2, ghost.y - side / 2, side, side, 2 / cam.scale);
          if (img) {
            ctx.save();
            ctx.clip();
            ctx.drawImage(img, ghost.x - side / 2, ghost.y - side / 2, side, side);
            ctx.restore();
          } else {
            ctx.fillStyle = colors.playlist;
            ctx.fill();
          }
          ctx.beginPath();
          roundRect(ctx, ghost.x - side / 2, ghost.y - side / 2, side, side, 2 / cam.scale);
          ctx.strokeStyle = colors.faint;
          ctx.lineWidth = 1 / cam.scale;
          ctx.stroke();
        }
      }

      if (peekNow.more > 0 && parent && parent.x !== undefined && parent.y !== undefined) {
        const label = `+${peekNow.more}`;
        const lx = parent.x;
        const ly = parent.y - parent.radius - 18 / cam.scale;
        ctx.globalAlpha = ghostAlpha * 1.4;
        ctx.font = `${11 / cam.scale}px ${colors.font}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = colors.muted;
        ctx.fillText(label, lx, ly);
      }
      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }, [selected, session, peek, hoveredKey]);

  useEffect(() => {
    draw();
  }, [draw, bootstrapping]);

  useEffect(() => {
    const onResize = () => draw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [draw]);

  function hitTest(clientX: number, clientY: number): SimNode | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const cam = cameraRef.current;
    const wx = (clientX - rect.left - rect.width / 2 - cam.x) / cam.scale;
    const wy = (clientY - rect.top - rect.height / 2 - cam.y) / cam.scale;
    let best: SimNode | null = null;
    let bestDist = Infinity;
    for (const node of nodesRef.current) {
      if (node.x === undefined || node.y === undefined) continue;
      const dx = node.x - wx;
      const dy = node.y - wy;
      const hitR = node.kind === "artist" ? node.radius : node.radius * 1.2;
      const d = Math.hypot(dx, dy);
      if (d <= hitR && d < bestDist) {
        best = node;
        bestDist = d;
      }
    }
    return best;
  }

  async function expandNode(node: SimNode, loadMore = false) {
    if (busyKey) return;
    const parentKey = node.key;

    // Snapshot peek satellites so real nodes open where the ghosts sat.
    const peekSnap =
      peekRef.current && peekRef.current.parentKey === parentKey ? peekRef.current : null;
    // Cancel any in-flight peek fetch and wipe ghosts immediately.
    peekFetchRef.current += 1;
    if (peekFadeRef.current !== null) {
      cancelAnimationFrame(peekFadeRef.current);
      peekFadeRef.current = null;
    }
    setPeek(null);
    setHoveredKey(null);
    hoverKeyRef.current = null;
    peekCacheRef.current.delete(parentKey);

    if (!loadMore && isExpanded(session, parentKey)) {
      persist(collapseExpansion(session, parentKey));
      draw();
      return;
    }
    const existing = session.expansions.find((e) => e.id === parentKey);
    const offset = loadMore ? (existing?.loaded ?? 0) : 0;
    setBusyKey(parentKey);
    try {
      if (peekSnap && !loadMore) {
        for (const ghost of peekSnap.ghosts) {
          pendingSpawnRef.current[ghost.key] = { x: ghost.x, y: ghost.y };
        }
      }
      const spawn =
        node.x !== undefined && node.y !== undefined ? { x: node.x, y: node.y } : { x: 0, y: 0 };
      if (node.kind === "artist") {
        const data = await fetchExploreArtistPlaylists(node.id, offset, PAGE);
        const next = applyExpandResult(
          session,
          parentKey,
          data.playlists,
          data.edges,
          offset + data.playlists.length,
          data.total,
          spawn,
          pendingSpawnRef.current,
        );
        persist(next);
      } else {
        const data = await fetchExplorePlaylistArtists(node.kind, node.id, offset, PAGE);
        const next = applyExpandResult(
          session,
          parentKey,
          data.artists,
          data.edges,
          offset + data.artists.length,
          data.total,
          spawn,
          pendingSpawnRef.current,
        );
        persist(next);
      }
      // Frame the expanded parent so newcomers aren't off-screen.
      if (node.x !== undefined && node.y !== undefined) {
        const cam = cameraRef.current;
        cameraRef.current = {
          ...cam,
          x: -node.x * cam.scale,
          y: -node.y * cam.scale,
        };
      }
      draw();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.message : "Failed to expand node");
    } finally {
      setBusyKey(null);
    }
  }

  function onPointerDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    const node = hitTest(e.clientX, e.clientY);
    if (node && e.button === 0) {
      dragRef.current = {
        mode: "node",
        key: node.key,
        lastX: e.clientX,
        lastY: e.clientY,
        moved: false,
      };
      node.fx = node.x ?? 0;
      node.fy = node.y ?? 0;
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button === 0 || e.button === 1) {
      dragRef.current = {
        mode: "pan",
        lastX: e.clientX,
        lastY: e.clientY,
        moved: false,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }

  function onPointerMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    const drag = dragRef.current;
    if (!drag) {
      const hit = hitTest(e.clientX, e.clientY);
      const nextKey = hit?.key ?? null;
      if (nextKey !== hoverKeyRef.current) setHoveredKey(nextKey);
      return;
    }
    const dx = e.clientX - drag.lastX;
    const dy = e.clientY - drag.lastY;
    if (Math.hypot(dx, dy) > 3) drag.moved = true;
    drag.lastX = e.clientX;
    drag.lastY = e.clientY;
    if (drag.mode === "pan") {
      cameraRef.current = {
        ...cameraRef.current,
        x: cameraRef.current.x + dx,
        y: cameraRef.current.y + dy,
      };
      draw();
      return;
    }
    if (drag.mode === "node" && drag.key) {
      const node = nodesRef.current.find((n) => n.key === drag.key);
      if (!node) return;
      const cam = cameraRef.current;
      const fx = (typeof node.fx === "number" ? node.fx : (node.x ?? 0)) + dx / cam.scale;
      const fy = (typeof node.fy === "number" ? node.fy : (node.y ?? 0)) + dy / cam.scale;
      node.fx = fx;
      node.fy = fy;
      simRef.current?.alpha(0.15).restart();
      draw();
    }
  }

  function onPointerLeave() {
    if (dragRef.current) return;
    setHoveredKey(null);
  }

  function onPointerUp(e: ReactPointerEvent<HTMLCanvasElement>) {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;

    if (drag.mode === "node" && drag.key) {
      const node = nodesRef.current.find((n) => n.key === drag.key);
      if (node && drag.moved && typeof node.fx === "number" && typeof node.fy === "number") {
        setPinned(kind, id, node.key, { x: node.fx, y: node.fy });
        persist({
          ...session,
          pinned: { ...session.pinned, [node.key]: { x: node.fx, y: node.fy } },
        });
      } else if (node && !drag.moved) {
        const now = Date.now();
        const last = lastClickRef.current;
        if (detailTimerRef.current) {
          clearTimeout(detailTimerRef.current);
          detailTimerRef.current = null;
        }
        // Double-click → expand (artist → playlists, playlist → artists).
        if (last && last.key === node.key && now - last.at < CLICK_DETAIL_MS + 80) {
          lastClickRef.current = null;
          void expandNode(node);
          return;
        }
        lastClickRef.current = { key: node.key, at: now };
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          setSelected((prev) =>
            prev.includes(node.key) ? prev.filter((k) => k !== node.key) : [...prev, node.key],
          );
          return;
        }
        setSelected([node.key]);
        // Defer detail so a double-click can cancel and expand instead.
        detailTimerRef.current = setTimeout(() => {
          detailTimerRef.current = null;
          openDetail({
            type:
              node.kind === "local"
                ? "local_playlist"
                : node.kind === "playlist"
                  ? "playlist"
                  : "artist",
            id: node.id,
            label: node.name ?? undefined,
          });
        }, CLICK_DETAIL_MS);
      }
    } else if (drag.mode === "pan" && !drag.moved) {
      setSelected([]);
    }
  }

  function onWheel(e: ReactWheelEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const cam = cameraRef.current;
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, cam.scale * factor));
    cameraRef.current = { ...cam, scale: next };
    draw();
  }

  function startOver() {
    clearSession(kind, id);
    setSelected([]);
    cameraRef.current = { x: 0, y: 0, scale: 1 };
    const blank = emptySession(kind, id, session.seedLabel);
    setSession(blank);
    setBootstrapping(true);
    void (async () => {
      try {
        const data = await fetchExplorePlaylistArtists(kind, id, 0, PAGE);
        const seedKey = exploreSeedKey(kind, id);
        let next = emptySession(kind, id, data.playlist.name ?? "Playlist");
        next.nodeMeta[seedKey] = {
          kind: data.playlist.kind,
          id: data.playlist.id,
          name: data.playlist.name,
          image_url: data.playlist.image_url,
          track_count: data.playlist.track_count,
        };
        next = applyExpandResult(
          next,
          seedKey,
          data.artists,
          data.edges,
          data.artists.length,
          data.total,
          { x: 0, y: 0 },
          pendingSpawnRef.current,
        );
        next = {
          ...next,
          pinned: { [seedKey]: { x: 0, y: 0 } },
        };
        persist(next);
      } catch (err) {
        toast.error(err instanceof ApiRequestError ? err.message : "Failed to reset explorer");
      } finally {
        setBootstrapping(false);
      }
    })();
  }

  function collapseOldest() {
    const oldest = session.expansionOrder[0];
    if (!oldest) return;
    // Never collapse the seed expansion via the warning — that wipes the seed ring.
    const seedKey = exploreSeedKey(kind, id);
    const victim =
      oldest === seedKey
        ? session.expansionOrder.find((k) => k !== seedKey)
        : oldest;
    if (!victim) return;
    persist(collapseExpansion(session, victim));
  }

  function openTracks() {
    const filter = buildOpenTracksFilter(session, selected);
    if (!filter) return;
    void navigate(`/all?filter=${encodeURIComponent(JSON.stringify(filter))}`);
  }

  const moreFor = (key: string) => {
    const exp = session.expansions.find((e) => e.id === key);
    if (!exp || exp.loaded >= exp.total) return 0;
    return exp.total - exp.loaded;
  };

  const selectedMore =
    selected.length === 1 ? moreFor(selected[0]!) : 0;
  const selectedNode =
    selected.length === 1 ? (session.nodeMeta[selected[0]!] ?? null) : null;
  const overBudget = session.nodeKeys.length > NODE_WARN;

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            Playlist explorer
          </p>
          <h1 className="truncate text-lg font-semibold tracking-tight">
            {session.nodeMeta[exploreSeedKey(kind, id)]?.name ?? session.seedLabel}
          </h1>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={startOver}>
          <RotateCcw className="size-3.5" aria-hidden />
          Start over
        </Button>
        {selected.length === 1 && selected[0] && (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busyKey !== null}
              onClick={() => {
                const node = nodesRef.current.find((n) => n.key === selected[0]);
                if (node) void expandNode(node);
              }}
            >
              <Network className="size-3.5" aria-hidden />
              {isExpanded(session, selected[0])
                ? "Collapse"
                : selectedNode?.kind === "artist"
                  ? "Show playlists"
                  : "Show artists"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                const meta = session.nodeMeta[selected[0]!];
                if (!meta) return;
                openDetail({
                  type:
                    meta.kind === "local"
                      ? "local_playlist"
                      : meta.kind === "playlist"
                        ? "playlist"
                        : "artist",
                  id: meta.id,
                  label: meta.name ?? undefined,
                });
              }}
            >
              <Info className="size-3.5" aria-hidden />
              Details
            </Button>
          </>
        )}
        {selected.length > 0 && (
          <Button type="button" variant="secondary" size="sm" onClick={openTracks}>
            <ListMusic className="size-3.5" aria-hidden />
            Open tracks in grid
          </Button>
        )}
        {selectedMore > 0 && selected[0] && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busyKey !== null}
            onClick={() => {
              const node = nodesRef.current.find((n) => n.key === selected[0]);
              if (node) void expandNode(node, true);
            }}
          >
            +{Math.min(PAGE, selectedMore)} more
          </Button>
        )}
      </header>

      <div ref={wrapRef} className="relative min-h-0 flex-1 bg-bg">
        {bootstrapping && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg/70 text-sm text-muted">
            Loading artists…
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="size-full touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerLeave}
          onWheel={onWheel}
        />

        <div className="pointer-events-none absolute right-3 bottom-3 flex flex-col items-end gap-2">
          <div
            className={cn(
              "rounded-md border border-line bg-panel/90 px-2.5 py-1.5 text-xs tabular-nums",
              overBudget ? "text-status-working" : "text-muted",
            )}
          >
            {session.nodeKeys.length.toLocaleString()} nodes
            {session.edgeKeys.length > 0 &&
              ` · ${session.edgeKeys.length.toLocaleString()} edges`}
          </div>
          {overBudget && (
            <div className="pointer-events-auto max-w-xs rounded-md border border-line bg-panel p-3 text-xs shadow-focus">
              <p className="flex items-start gap-2 text-muted">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-status-working" aria-hidden />
                Large graph — collapse the oldest expansion to keep it traversable.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={collapseOldest}
              >
                Collapse oldest
              </Button>
            </div>
          )}
        </div>

        <p className="pointer-events-none absolute bottom-3 left-3 max-w-sm text-xs text-faint">
          Hover to peek · double-click to expand · click opens details
        </p>
      </div>
    </div>
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
